import { useEffect, useRef } from "react";
import { DollarSign } from "lucide-react";
import { menuItems } from "./HeaderData";
import { Link, useLocation } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import { BsWhatsapp } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { BiLogoPlayStore } from "react-icons/bi";
import {
  closeAllSidebars,
  toggleLeftSidebar,
} from "../../redux/slices/sidebarSlice";
import {
  isSignupUser,
  selectSite,
  SelectUserLockApplications,
} from "../../utils/helper/commonSelectors";
import GoogleTranslate from "../../../CommonComponents/GoogleTranslate";
import SidebarMenuItem from "./SidebarMenuItem";

const siteApkDetails = {
  "asianexchange.club": {
    href: "/apk/asian.apk",
    download: "asian.apk"
  }
}

const Sidebar = () => {
  const siteDetails = useSelector(selectSite());
  const location = useLocation();
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state?.sidebar?.isLeftOpen);
  const sidebarRef = useRef(null);
  const accountType = useSelector((state) => state?.account?.accountType) || "";
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const lockData = useSelector(SelectUserLockApplications());
    const signupUser = useSelector(isSignupUser());

  const sportLockStatus = {
    cricket: lockData?.cricket,
    soccer: lockData?.soccer,
    tennis: lockData?.tennis,
    kabaddi: lockData?.kabaddi,
    horse: lockData?.horse,
    greyhound: lockData?.greyhound,
  };
  const filtermenuItems = menuItems.filter((e) => {
    return !sportLockStatus[e?.label?.toLowerCase()];
  });
  const handleDownload = () => {
    const linkDetails = siteApkDetails?.[window?.location?.hostname] || { href: "", download: "" };
    const link = document.createElement("a");
    link.href = linkDetails?.href;
    link.download = linkDetails?.download;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleDrawer = () => {
    dispatch(toggleLeftSidebar());
  };
  useEffect(() => {
    if (isOpen) toggleDrawer();
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        dispatch(closeAllSidebars()); // Close both sidebars
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "calc(100vh - 120px)"; // Prevents scrolling on iOS
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen]);

  return (
    <div>
      {isOpen && (
        <div
          className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-40"
          onClick={() => dispatch(closeAllSidebars())}
        ></div>
      )}
      <nav
        ref={sidebarRef}
        className={`scroll-p-4 pb-4 fixed bg-white top-0 left-0 h-full w-1/2 md:hidden z-50 overflow-y-auto hide-scrollbar transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        style={{ height: "calc(100vh - 52px)", paddingBottom: "100px" }}
      >
        {/* ✅ Close Sidebar Button */}
        <button
          onClick={() => {
            toggleDrawer();
          }}
          className="text-black p-1.5 rounded-full font-extrabold block md:hidden fixed right-2 z-50"
        >
          {isOpen && <IoClose size={16} className="font-semibold text-white" />}
        </button>

        {/* Popular Section */}
        <div className="p-1.5 bg-[#27A6C3] text-white text-[14px] font-[700] font-sans">
          Popular
        </div>

        <div className="pt-1">
          {/* Deposit & Withdraw Buttons */}

          <GoogleTranslate />

          {accountType === "real" && isLoggedIn && signupUser ? (
            <div className="flex items-center justify-between py-2 px-4 cursor-pointer text-black hover:text-white">
              <div className="flex flex-col justify-center items-center space-y-2 w-full">
                <Link
                  to="/deposit"
                  onClick={toggleDrawer}
                  className="flex items-center justify-center space-x-1 p-1.5 px-3.5 w-full bg-green-700 text-white rounded-md text-xs hover:bg-green-600 transition-colors duration-300"
                >
                  <DollarSign size={15} className="text-yellow" />
                  <span className="font-semibold">Deposit</span>
                </Link>
                <Link
                  to="/withdraw"
                  onClick={toggleDrawer}
                  className="flex items-center justify-center space-x-1 w-full bg-red-500 text-white p-1.5 px-3.5 rounded-md text-xs hover:bg-blue-600 transition-colors duration-300"
                >
                  <DollarSign size={15} className="text-yellow" />
                  <span className="font-semibold">Withdrawal</span>
                </Link>
              </div>
            </div>
          ) : (
            ""
          )}

          {/* Navigation Menu */}
          <div className="pt-4 pb-8 space-y-2">
            {filtermenuItems?.map((item) => (
              <SidebarMenuItem key={item.label} item={item} isOpen={isOpen} toggleDrawer={toggleDrawer} />
            ))}
          </div>

          {/* WhatsApp Support */}
          {siteDetails?.whatsappSupport && (
            <div className="flex items-center justify-between py-2 px-4 cursor-pointer text-black hover:text-white">
              <div className="flex flex-col justify-center items-center space-y-2 w-full mb-4">
                <Link
                  to="/"
                  className="flex items-center justify-center space-x-1 w-full bg-[#43C554] text-white p-1.5 px-3.5 rounded-md hover:bg-blue-600 transition-colors duration-300"
                  onClick={() =>
                    window.open(
                      `https://wa.me/${siteDetails.whatsappSupport.replaceAll(
                        " ",
                        ""
                      )}`,
                      "_blank"
                    )
                  }
                >
                  <BsWhatsapp size={15} className="text-white" />
                  <span className="font-semibold text-[9px] sm:text-xs">
                    Whatsapp Support
                  </span>
                </Link>
              </div>
            </div>
          )}

          {
            siteApkDetails?.[window?.location?.hostname] && <div className="flex flex-col items-center justify-center">
              <div className="flex flex-col px-4 sm:flex-row items-center justify-center gap-4 w-full">
                <Link
                  to="#"
                  className="flex items-center justify-center space-x-2 w-full sm:w-auto bg-[#43C554] text-white py-2 px-4 rounded-md hover:bg-green-600 transition-colors duration-300"
                  onClick={handleDownload}
                >
                  <BiLogoPlayStore size={20} className="text-white" />
                  <div className="flex flex-col items-start">
                    <span className="font-semibold text-sm">Google Play</span>
                  </div>
                </Link>
              </div>
            </div>
          }
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
